import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Calendar,
  Clock,
  Users,
  DollarSign,
  Link as LinkIcon,
  Settings,
  BarChart3,
  MessageSquare,
  Edit,
  Mail,
  Trash2,
  FileText,
  Share,
  Download,
} from "lucide-react";
import { EventsService } from "@/api/events";
import { useToast } from "@/hooks/use-toast";
import { getCurrentClientData } from "@/services/authServices";
import { number } from "zod";
import { EmailModal } from "@/components/general/EmailModal";

// Mock data - in a real app this would come from an API
const mockEventData = {
  id: "1",
  title: "Tech Innovation Summit 2023",
  description:
    "Join industry leaders for discussions on emerging technologies and digital transformation strategies.",
  date: "2023-12-15",
  startTime: "09:00",
  endTime: "17:00",
  location: "San Francisco Convention Center",
  isVirtual: false,
  category: "Technology",
  ticketPrice: "299.00",
  maxAttendees: 400,
  currentAttendees: 234,
  ticketsSold: 234,
  revenue: 69966,
  publishStatus: "published",
  image:
    "https://images.unsplash.com/photo-1540575467063-178a50c2df87?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80",
};

interface ViewEventProps {
  days_until_event: number;
  event_id: string;
  max_attendees: number;
  status: string;
  total_revenue: number;
  total_registrations: number;
}

interface EventProps {
  location: string;
  created_at: string;
  event_image: string;
  event_title: string;
  event_description: string;
  event_time: string;
  event_id: string;
  category: string;
  image_url: string[];
  event_date: string;
  virtual_event: boolean;
  start_time: string;
  end_time: string;
}

const EventDashboard = () => {
  const { eventId } = useParams();
  const { getEvents1, viewEvent, listCurrentAttendees, exportAttendees } =
    EventsService;
  const loggedInUser = getCurrentClientData();
  const [attendees, setAttendees] = useState([]);
  const [eventList, setEventlist] = useState<ViewEventProps>();
  const [event, setEvent] = useState<EventProps>();
  const [copied, setCopied] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);

  const [isOpen, setIsOpen] = useState(false);

  const shareUrl = `${window.location.origin}/event/${eventId}/register`;

  const [activeTab, setActiveTab] = useState("overview");
  const { toast } = useToast();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // if (loggedInUser) {
    loadAllEventsFn();
    loadViewEventFn();
    loadListCurrentAttendees();
    // } else {
    //   setLoading(false);
    // }
  }, []);

  const loadAllEventsFn = async () => {
    setLoading(true);

    try {
      const res: any = await getEvents1(eventId, loggedInUser?.token);

      if (res.statusCode === 200 || res.statusCode === 201) {
        setEvent(res.body.data);
      } else {
        toast({
          title: "Error",
          description:
            res.body.message || "There was an error creating your event.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "There was an error creating your event.",
        variant: "destructive",
      });
      return;
    } finally {
      setLoading(false);
    }
  };

  const loadViewEventFn = async () => {
    setLoading(true);

    try {
      const res: any = await viewEvent(eventId, loggedInUser?.token);

      if (res.statusCode === 200 || res.statusCode === 201) {
        setEventlist(res.body);
      } else {
        toast({
          title: "Error",
          description:
            res.body.message || "There was an error creating your event.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "There was an error creating your event.",
        variant: "destructive",
      });
      return;
    } finally {
      setLoading(false);
    }
  };

  const loadListCurrentAttendees = async () => {
    setLoading(true);

    try {
      const res: any = await listCurrentAttendees(eventId, loggedInUser?.token);

      if (res.statusCode === 200 || res.statusCode === 201) {
        setAttendees(res.body.attendees);
      } else {
        toast({
          title: "Error",
          description:
            res.body.message || "There was an error creating your event.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "There was an error creating your event.",
        variant: "destructive",
      });
      return;
    } finally {
      setLoading(false);
    }
  };
  // In a real app, you would fetch the event data based on the eventId

  const soldOutPercentage =
    (eventList?.total_registrations / eventList?.max_attendees) * 100;

  const LoadingSpinner = () => (
    <div className="flex items-center justify-center h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
    </div>
  );

  const handleExport = async () => {
    setLoadingExport(true);
    setDownloadProgress(70); // <- Add this if you want to track progress in UI

    try {
      const res: any = await exportAttendees(eventId, loggedInUser?.token);

      if (res.statusCode === 200 || res.statusCode === 201) {
        const contentLength = parseInt(
          res.headers?.["content-length"] ||
            res.headers?.["Content-Length"] ||
            "0",
          10
        );

        const reader = res.body?.getReader?.();
        const chunks = [];
        let receivedLength = 0;

        if (reader) {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            chunks.push(value);
            receivedLength += value.length;

            if (contentLength) {
              const percent = Math.round(
                (receivedLength / contentLength) * 100
              );
              setDownloadProgress(percent);
            }
          }

          const blob = new Blob(chunks, { type: "text/csv" });
          const url = window.URL.createObjectURL(blob);

          const a = document.createElement("a");
          a.href = url;

          const contentDisposition =
            res.headers?.["content-disposition"] ||
            res.headers?.["Content-Disposition"];
          const fileNameMatch = contentDisposition?.match(/filename="(.+)"/);
          const filename = fileNameMatch?.[1] || "attendees.csv";

          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        } else {
          // Fallback if not a stream
          const blob = new Blob([res.body], { type: "text/csv" });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = "attendees.csv";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }

        setDownloadProgress(100);
        setLoadingExport(false);
      } else {
        throw new Error(res.body?.message || "Failed to export.");
      }
    } catch (error) {
      setLoadingExport(false);
      setDownloadProgress(0);
      toast({
        title: "Error",
        description: "There was an error exporting attendees.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow bg-gray-50">
        {/* Page Header */}
        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            <div className="bg-cecure-900 text-white py-8">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h1 className="text-2xl font-bold capitalize">
                        {event?.event_title}
                      </h1>
                      {/* <Badge
                        variant={
                          eventList.publishStatus === "published"
                            ? "default"
                            : "outline"
                        }
                      >
                        {eventList.publishStatus === "published"
                          ? "Published"
                          : "Draft"}
                      </Badge> */}
                    </div>
                    <div className="flex items-center mt-2 text-sm">
                      <Calendar className="h-4 w-4 mr-1" />
                      <span className="mr-4">
                        {new Date(event?.event_date).toLocaleDateString(
                          "en-US",
                          {
                            weekday: "long",
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          }
                        )}
                      </span>
                      <Clock className="h-4 w-4 mr-1" />
                      <span>
                        {event?.start_time} - {event?.end_time}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    {/* <Link to={`/event/${eventId}/register`}>
                      <Button
                        variant="outline"
                        className="bg-white text-cecure-900 hover:bg-gray-100"
                      >
                        Preview
                      </Button>
                    </Link> */}
                    <Link to={`/create?edit=${eventId}`}>
                      <Button
                        variant="outline"
                        className="bg-white text-cecure-900 hover:bg-gray-100"
                      >
                        <Edit className="mr-2 h-4 w-4" />
                        Edit Event
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="mb-8">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="attendees">Attendees</TabsTrigger>
                  <TabsTrigger value="registration">Registration</TabsTrigger>
                  <TabsTrigger value="communications">
                    Communications
                  </TabsTrigger>
                  <TabsTrigger disabled={true} value="reports">
                    Reports
                  </TabsTrigger>
                  <TabsTrigger value="settings">Settings</TabsTrigger>
                </TabsList>

                {/* Overview Tab */}
                <TabsContent value="overview" className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <Card>
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-500">
                              Registrations
                            </p>
                            <h3 className="text-2xl font-bold mt-1">
                              {eventList?.total_registrations}
                            </h3>
                          </div>
                          <Users className="h-10 w-10 text-cecure-700 opacity-80" />
                        </div>
                        <div className="mt-4">
                          <div className="flex justify-between text-sm mb-1">
                            <span>{eventList?.total_registrations} sold</span>
                            <span>{eventList?.max_attendees} capacity</span>
                          </div>
                          <Progress value={soldOutPercentage} className="h-2" />
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-500">
                              Revenue
                            </p>
                            <h3 className="text-2xl font-bold mt-1">
                              $
                              {(eventList?.total_revenue ?? 0).toLocaleString()}
                            </h3>
                          </div>
                          <DollarSign className="h-10 w-10 text-cecure-700 opacity-80" />
                        </div>

                        {/* <div className="mt-4 text-sm text-gray-500">
                          <p>
                            Average ticket: $0
                            {(
                              eventList.revenue / eventList.ticketsSold
                            ).toFixed(2)}
                          </p>
                        </div> */}
                      </CardContent>
                    </Card>

                    <Card>
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-500">
                              Event Status
                            </p>
                            <h3 className="text-xl font-bold mt-1">
                              {soldOutPercentage >= 100
                                ? "Sold Out"
                                : "On Sale"}
                            </h3>
                          </div>
                          <Calendar className="h-10 w-10 text-cecure-700 opacity-80" />
                        </div>
                        <div className="mt-4 text-sm text-gray-500">
                          <p>
                            {/* {Math.ceil(
                              (new Date(eventList?.date).getTime() -
                                new Date().getTime()) /
                                (1000 * 60 * 60 * 24)
                            )}{" "} */}
                            {eventList?.days_until_event} days until event
                          </p>
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-500">
                              Share Event
                            </p>
                            <h3 className="text-xl font-bold mt-1">
                              Promote Now
                            </h3>
                          </div>
                          <Share
                            className="h-10 w-10 text-cecure-700 opacity-80 cursor-pointer"
                            onClick={handleCopy}
                          />
                        </div>
                        <div className="mt-4">
                          <Input
                            value={shareUrl}
                            readOnly
                            onClick={(e) => e.currentTarget.select()}
                          />
                          {copied && (
                            <p className="text-sm text-green-600 mt-2">
                              Copied!
                            </p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <Card className="lg:col-span-2">
                      <CardHeader>
                        <CardTitle>Recent Activity</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {/* <div className="space-y-4 ">No recent activity</div> */}
                        <div className="space-y-4">
                          {attendees.length > 0 ? (
                            attendees.map((i) => (
                              <div
                                key={i?.registered_at}
                                className="border-b pb-3 last:border-0"
                              >
                                <div className="flex justify-between items-start">
                                  <div>
                                    <p className="font-medium">
                                      New registration: {i?.name}
                                    </p>
                                    <p className="text-sm text-gray-500">
                                      {/* Purchased 1 VIP ticket for $0 */}
                                      Purchased {i?.ticket_type} ticket for $
                                      {i?.price}
                                    </p>
                                  </div>
                                  <span className="text-xs text-gray-500">
                                    {new Date(
                                      i?.registered_at
                                    ).toLocaleTimeString()}
                                  </span>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="space-y-4 ">No recent activity</div>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>Quick Actions</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <Button
                          onClick={() => setIsOpen(true)}
                          className="w-full justify-start"
                          variant="outline"
                        >
                          <Mail className="mr-2 h-4 w-4" />
                          Email Attendees
                        </Button>
                        <Button
                          onClick={handleExport}
                          disabled={loadingExport}
                          className="w-full justify-start"
                          variant="outline"
                        >
                          {loadingExport ? (
                            <Progress
                              value={downloadProgress}
                              className="h-2"
                            />
                          ) : (
                            <>
                              <Download className="mr-2 h-4 w-4" />
                              Export Attendee List
                            </>
                          )}
                        </Button>
                        <Button
                          disabled={true}
                          className="w-full justify-start"
                          variant="outline"
                        >
                          <FileText className="mr-2 h-4 w-4" />
                          Generate Report
                        </Button>
                        {/* <Link to={`/event/${eventId}/attendees`}>
                          <Button
                            className="w-full justify-start"
                            variant="outline"
                          >
                            <Users className="mr-2 h-4 w-4" />
                            Manage Attendees
                          </Button>
                        </Link> */}
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                {/* Other tabs would be implemented here */}
                <TabsContent value="attendees">
                  <Card>
                    <CardContent className="p-6">
                      <p className="mb-4">
                        Manage your event attendees. View a complete list in the
                        Attendee Management page.
                      </p>
                      <Link to={`/event/${eventId}/attendees`}>
                        <Button>
                          <Users className="mr-2 h-4 w-4" />
                          Go to Attendee Management
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="registration">
                  <Card>
                    <CardContent className="p-6">
                      <p className="mb-4">
                        Configure registration forms and ticket types.
                      </p>
                      <Link to={`/create?edit=${eventId}`}>
                        <Button>
                          <FileText className="mr-2 h-4 w-4" />
                          Edit Registration Forms
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="communications">
                  <Card>
                    <CardContent className="p-6">
                      <p className="mb-4">
                        Send emails to your attendees and manage communication
                        templates.
                      </p>
                      <Button onClick={() => setIsOpen(true)}>
                        <Mail className="mr-2 h-4 w-4" />
                        Create New Email
                      </Button>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="reports">
                  <Card>
                    <CardContent className="p-6">
                      <p className="mb-4">
                        View analytics and generate reports about your event.
                      </p>
                      <Button>
                        <BarChart3 className="mr-2 h-4 w-4" />
                        Generate Reports
                      </Button>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="settings">
                  <Card>
                    <CardContent className="p-6">
                      <p className="mb-4">
                        Configure event settings, payment options, and
                        integrations.
                      </p>
                      <Button>
                        <Settings className="mr-2 h-4 w-4" />
                        Event Settings
                      </Button>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>

            <EmailModal
              open={isOpen}
              onOpenChange={setIsOpen}
              eventId={eventId}
            />
          </>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default EventDashboard;
